import * as React from 'react';
import { useUid, useUncontrolled } from '@/utils';

export interface ColorFieldProps {
  /** The HTML ID attribute to be applied to the color field input element (optional). */
  id?: string;
  /** The name attribute to be applied to the color field input element (optional). */
  name?: string;
  /** Whether the color field input should be disabled (optional). */
  disabled?: boolean;
  /** The current value of the color field input (optional). */
  value?: string;
  /** The default value of the color field input (optional). */
  defaultValue?: string;
  /**
   * A function to be called when the value of the color field input changes (optional).
   * The function receives the new color of the input as a string.
   */
  onChange?(value: string): void;
  /** The label to be displayed above the color field input (optional). */
  label?: React.ReactNode;
  /** Help text to be displayed below the color field input (optional). */
  helpText?: React.ReactNode;
  /** CSS class names to be applied to the label element (optional). */
  labelClassName?: string;
}

export const ColorField = React.forwardRef<HTMLInputElement, ColorFieldProps>((props, ref) => {
  const { id, label, name, disabled, helpText, labelClassName, onChange, value, defaultValue } = props;
  const [internalValue, setInternalValue] = useUncontrolled({
    value,
    defaultValue,
    finalValue: '',
    onChange,
  });
  const uid = useUid(id);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(event.currentTarget.value);
  };

  return (
    <label htmlFor={uid}>
      <p className={labelClassName}>{label}</p>
      <div className="merged-inputs">
        <div className="color-picker merge-right">
          <span className="color-preview" style={{ backgroundColor: internalValue }} />
          <input
            id={uid}
            ref={ref}
            type="color"
            name={name}
            disabled={disabled}
            onChange={handleChange}
            value={internalValue}
          />
        </div>
        <input type="text" className="merge-left" value={internalValue} onChange={handleChange} />
      </div>
      <small>{helpText}</small>
    </label>
  );
});

ColorField.displayName = 'ColorField';
